import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Settings, Bot, PanelRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Urls } from '@/lib/navigation';
import { trackSettingsOpened } from '@/analytics';
import navIcon from '@/assets/nav-icon.svg';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import type { SessionSummary } from '@/api/types';
import { CountBadge } from './labels/CountBadge';

function IconBtn({
  icon: Icon,
  label,
  onClick,
  active = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/[0.06]',
            active && 'bg-white/[0.08] text-zinc-200',
          )}
        >
          <Icon className="h-[18px] w-[18px] text-zinc-400" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={12}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function IconLink({
  icon: Icon,
  label,
  to,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  to: string;
  onClick?: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={to}
          onClick={onClick}
          className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/[0.06]"
        >
          <Icon className="h-[18px] w-[18px] text-zinc-400" />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={12}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export interface AgentIslandProps {
  sessions: SessionSummary[];
  isAuthenticated: boolean;
  onHome: () => void;
  artifactVisible: boolean;
  artifactOpen: boolean;
  onArtifactToggle: () => void;
}

export const AgentIsland = memo(function AgentIsland({
  sessions,
  isAuthenticated,
  onHome,
  artifactVisible,
  artifactOpen,
  onArtifactToggle,
}: AgentIslandProps) {
  const runningCount = useMemo(
    () => sessions.reduce((acc, s) => acc + (s.runtime_status === 'on' ? 1 : 0), 0),
    [sessions],
  );

  return (
    <>
      {/* Floating nav island — desktop only (mobile equivalent lives in ChatInput) */}
      <div className="fixed left-6 top-1/2 z-50 hidden -translate-y-1/2 desktop:block">
        <div className="overflow-hidden rounded-full border border-white/[0.06] bg-zinc-900/95 shadow-xl backdrop-blur-xl">
          <div className="flex flex-col items-center gap-[4.4px] p-1.5">
            {isAuthenticated ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={Urls.demos()}
                      className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/[0.06]"
                    >
                      <img src={navIcon} alt="Logo" className="h-5 w-5 object-contain invert" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={12}>
                    Demos
                  </TooltipContent>
                </Tooltip>

                <div className="h-px w-5 bg-white/[0.06]" />

                <IconBtn icon={Plus} label="New Agent" onClick={onHome} />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={Urls.agents()}
                      className="relative flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/[0.06]"
                    >
                      <Bot size={22} className="text-zinc-400" />
                      <CountBadge count={runningCount} className="right-1.5 top-1.5" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={12}>
                    Agents
                  </TooltipContent>
                </Tooltip>

                {artifactVisible && (
                  <IconBtn
                    icon={PanelRight}
                    label="Panel"
                    onClick={onArtifactToggle}
                    active={artifactOpen}
                  />
                )}

                <div className="h-px w-5 bg-white/[0.06]" />

                <IconLink icon={Settings} label="Settings" to={Urls.settings()} onClick={() => trackSettingsOpened('general')} />
              </>
            ) : (
              <>
                <IconBtn icon={Plus} label="New Agent" onClick={onHome} />
                {artifactVisible && (
                  <IconBtn
                    icon={PanelRight}
                    label="Panel"
                    onClick={onArtifactToggle}
                    active={artifactOpen}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

AgentIsland.displayName = 'AgentIsland';
